import rawExpectedMessengerConfig from "../brand-config/generated.messenger-config.json" with { type: "json" };
import type {
	AuthEndpointUrlParams,
	AuthenticatedConfig,
	DiscoveryAPI,
	Environment,
	MessagingConfig,
	MessengerConfig,
	PostMessageData,
	GenesisAuthParams,
	AuthTokenResponse,
} from "./types";

export const MESSENGER_CONFIG = rawExpectedMessengerConfig as MessagingConfig;

export function getGuestConfig(environment: Environment): MessengerConfig {
	return MESSENGER_CONFIG.environments[environment].guest;
}

/** Genesys web-deployment config CDN for the prod-apse2 (Australia) region — the same
 *  origin the Genesys bootstrap fetches its deployment config.json from. Matches the
 *  hardcoded `environment: "prod-apse2"` used by createGenesis in message-widget.ts. */
const GENESYS_CONFIG_CDN_ORIGIN = "https://api-cdn.mypurecloud.com.au";

/** The `status` value the deployment config reports when the deployment is enabled. */
const ACTIVE_DEPLOYMENT_STATUS = "Active";

/** How long checkIsDeploymentActive waits for the deployment config before giving up.
 *  The wrapper awaits this check BEFORE arming LOADING_TIMEOUT_MS, so a stalled request
 *  with no timeout would strand the user on the spinner — the exact indefinite hang this
 *  feature exists to prevent. On timeout the fetch is aborted and the deployment is
 *  treated as unavailable. */
const DEPLOYMENT_CONFIG_FETCH_TIMEOUT_MS = 10_000;

/**
 * Returns true only when the Genesys web-messaging deployment is confirmed Active.
 *
 * The Genesys bootstrap boots the messenger from this same config.json; when the
 * deployment is disabled in the Genesys admin, Messenger never boots and the wrapper
 * hangs until LOADING_TIMEOUT_MS. Fetching it ourselves lets us detect the inactive /
 * unavailable state up front and disconnect immediately.
 *
 * Anything other than a confirmed Active status counts as not-active: a non-2xx response
 * (e.g. 403 when the CDN stops serving a disabled deployment), a body whose `status`
 * isn't "Active", a fetch timeout, or a network/parse failure. In every such case the SDK
 * would fail to boot from the same config anyway, so failing fast is safe.
 *
 * `cache: "no-store"` is required so a disabled deployment can't be masked by a stale
 * previously-Active config sitting in the browser HTTP cache.
 */
export async function checkIsDeploymentActive(deploymentId: string): Promise<boolean> {
	const configUrl = `${GENESYS_CONFIG_CDN_ORIGIN}/webdeployments/v1/deployments/${encodeURIComponent(deploymentId)}/config.json`;
	const abortController = new AbortController();
	const timeoutId = setTimeout(() => abortController.abort(), DEPLOYMENT_CONFIG_FETCH_TIMEOUT_MS);
	try {
		const response = await fetch(configUrl, { signal: abortController.signal, cache: "no-store" });
		if (!response.ok) {
			console.error(
				`Genesys deployment config returned HTTP ${response.status} — treating deployment as unavailable`,
			);
			return false;
		}
		const config = (await response.json()) as { status?: string };
		const isActive = config?.status === ACTIVE_DEPLOYMENT_STATUS;
		if (!isActive) {
			console.error(
				`Genesys deployment status is "${config?.status}" (expected "${ACTIVE_DEPLOYMENT_STATUS}") — treating deployment as unavailable`,
			);
		}
		return isActive;
	} catch (error) {
		console.error("Failed to fetch Genesys deployment config — treating deployment as unavailable:", error);
		return false;
	} finally {
		clearTimeout(timeoutId);
	}
}

export function getAuthenticatedConfig(environment: Environment): AuthenticatedConfig {
	return (
		MESSENGER_CONFIG.environments[environment].authenticated ?? {
			deploymentId: "authenticated-deployment-ID-unavailable",
			clientId: "authenticated-client-ID-unavailable",
			discoveryUrl: "authenticated-discovery-URL-unavailable",
		}
	);
}

/**
 * Generates a Code Challenge from a Code Verifier using SHA-256
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
	const encoder = new TextEncoder();
	const data = encoder.encode(codeVerifier);
	const digest = await window.crypto.subtle.digest("SHA-256", data);

	return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64 URL encoding (different from standard Base64)
 */
export function base64UrlEncode(array: Uint8Array): string {
	const str = String.fromCharCode.apply(null, Array.from(array));
	const base64 = btoa(str);
	return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function checkIsAuthenticated(): boolean {
	const urlParams = new URLSearchParams(window.location.search);
	// example URL: http://localhost:23068/?auth=true
	const isAuthenticated = urlParams.get("auth") === "true";
	return isAuthenticated;
}

/**
 * Detects the current environment based on the hostname.
 * - `netlify`: any *.netlify.app host (production preview site, deploy-previews,
 *   branch deploys) and `localhost`.
 * - `dev`: webchat.dev.hollard.com.au (uses the Hollard B2C dev policy)
 * - `prod`: webchat.hollard.com.au (uses the Hollard B2C dev policy)
 * - Genuinely-unknown hostnames hit a generic "no environment mapping defined" throw.
 */
export function getEnvironment(): Environment {
	const hostname = window.location.hostname;
	const environment = hostnameToEnvironment(hostname);
	console.log(`Environment detected: ${environment} (hostname: ${hostname})`);
	return environment;
}

function hostnameToEnvironment(hostname: string): Environment {
	if (hostname.endsWith(".netlify.app") || hostname === "localhost") return "netlify";
	if (hostname.includes(".dev.hollard.com.au")) return "dev";
	if (hostname.includes(".hollard.com.au")) return "prod";
	throw new Error(`Unknown hostname "${hostname}" — no environment mapping defined.`);
}

interface OverrideParams {
	isAuthenticated?: boolean;
	environment?: Environment;
}

export function getDeploymentId({ isAuthenticated, environment }: OverrideParams = {}): string {
	const authenticated = isAuthenticated ?? checkIsAuthenticated();
	const env = environment ?? getEnvironment();

	const envConfig = MESSENGER_CONFIG.environments[env];
	const authenticatedId = envConfig.authenticated?.deploymentId ?? "authenticated-deployment-ID-unavailable";
	return authenticated ? authenticatedId : envConfig.guest.deploymentId;
}

/**
 * Generates a cryptographically random code verifier for PKCE.
 */
export function generateCodeVerifier(): string {
	const array = new Uint8Array(32);
	crypto.getRandomValues(array);
	return base64UrlEncode(array);
}

/**
 * Builds the OIDC authorization URL with PKCE parameters.
 */
export function buildAuthorizationUrl(
	authConfig: AuthenticatedConfig,
	authEndpoint: string,
	redirectUri: string,
	state: string,
	codeChallenge: string,
): string {
	const params: AuthEndpointUrlParams = {
		p: authConfig.b2cPolicy ?? "",
		client_id: authConfig.clientId,
		response_type: "code",
		redirect_uri: redirectUri,
		scope: "openid profile email offline_access",
		response_mode: "query",
		state,
		code_challenge: codeChallenge,
		code_challenge_method: "S256",
		// Force the IdP to render the login form even if an SSO cookie is present,
		// so switching users in the same browser does not silently re-auth as the previous user.
		prompt: "login",
	};

	console.log("Building OIDC authorisation URL:", {
		authEndpoint,
		params,
	});

	const searchParams = new URLSearchParams(params as unknown as Record<string, string>);
	return `${authEndpoint}?${searchParams.toString()}`;
}

export async function fetchAuthDiscoveryData(environment: Environment): Promise<DiscoveryAPI> {
	const authConfig = getAuthenticatedConfig(environment);
	return await fetch(authConfig.discoveryUrl).then((res) => res.json());
}

// --- OIDC Popup Flow ---

/**
 * Opens an OIDC login popup and returns the auth code via a Promise.
 * The popup redirects to /auth-callback.html which sends the code back via postMessage.
 * Works with both Auth0 (netlify) and Hollard B2C (dev) OIDC providers. The
 * provider in use is determined by the `authenticated` config block for the
 * current environment, not by the environment name itself.
 *
 * If the initial popup is blocked by the browser, this function posts POPUP_BLOCKED
 * (with the auth URL) to window.parent and waits for the parent (widget-wrapper) to
 * open the popup on behalf of the user and return the result as AUTH_RESULT.
 */
export async function openOidcPopup(authEndpoint: string, environment: Environment): Promise<GenesisAuthParams> {
	const authConfig = getAuthenticatedConfig(environment);

	// Generate PKCE credentials
	const codeVerifier = generateCodeVerifier();
	const codeChallenge = await generateCodeChallenge(codeVerifier);
	const state = crypto.randomUUID();

	// The redirect URI points to our callback page (same origin)
	const redirectUri = `${window.location.origin}/auth-callback.html`;

	// Build OIDC authorization URL
	const authUrl = buildAuthorizationUrl(authConfig, authEndpoint, redirectUri, state, codeChallenge);

	console.log(`Opening OIDC login popup (${environment})...`);

	return new Promise((resolve, reject) => {
		const popup = openPopupWindow(authUrl, "auth0-login");

		if (!popup) {
			// Popup was blocked — delegate to the parent (widget-wrapper) window.
			// It will show a button the user can click (a real user gesture) to open the popup,
			// then forward the auth callback result back to us as AUTH_RESULT.
			console.warn("Popup was blocked — requesting parent window to handle auth");
			window.parent.postMessage({ type: "ACTION", action: "POPUP_BLOCKED", authUrl }, window.origin);

			// Wait for the parent to send AUTH_RESULT (success) or AUTH_CANCELLED (popup closed)
			function handleParentAuthMessage(event: MessageEvent<PostMessageData>) {
				if (event.source !== window.parent) return;
				if (event.origin !== window.location.origin) return;
				const data = event.data;

				if (data.action === "AUTH_RESULT") {
					window.removeEventListener("message", handleParentAuthMessage);
					if (!data.code) {
						reject(new Error("Authentication failed: no authorisation code received"));
						return;
					}
					if (data.state !== state) {
						reject(new Error("Authentication failed: state mismatch"));
						return;
					}
					resolve({
						authCode: data.code,
						redirectUri,
						codeVerifier,
						state: data.state,
					} satisfies GenesisAuthParams);
				} else if (data.action === "AUTH_CANCELLED") {
					window.removeEventListener("message", handleParentAuthMessage);
					reject(new Error("Authentication cancelled: popup was closed"));
				}
			}

			window.addEventListener("message", handleParentAuthMessage);
			return;
		}

		// Popup opened directly — listen for the callback and poll for closure
		function onMessage(event: MessageEvent<PostMessageData>) {
			if (event.origin !== window.location.origin) return;
			if (event.data?.action !== "AUTH_RESULT") return;

			window.removeEventListener("message", onMessage);
			clearInterval(pollTimer);

			const { code, state: returnedState, error, errorDescription } = event.data;
			console.log("Data received from OIDC callback:", event.data);

			if (error) {
				reject(new Error(`OIDC error: [${error}] ${errorDescription}`));
				return;
			}
			if (returnedState !== state) {
				reject(new Error("Authentication failed: state mismatch"));
				return;
			}
			if (!code) {
				reject(new Error("Authentication failed: no authorisation code received"));
				return;
			}

			const authReturnProperties: GenesisAuthParams = {
				authCode: code,
				redirectUri,
				codeVerifier,
				state: returnedState,
			};
			console.log("Auth properties retrieved from OIDC popup:", authReturnProperties);
			resolve(authReturnProperties);
		}

		window.addEventListener("message", onMessage);

		// Poll to detect if user closed the popup without completing login
		const pollTimer = setInterval(() => {
			if (popup.closed) {
				clearInterval(pollTimer);
				window.removeEventListener("message", onMessage);
				reject(new Error("Authentication cancelled: popup was closed"));
			}
		}, 500);
	});
}

function openPopupWindow(popupUrl: string, target: string = "_blank"): Window | null {
	return window.open(popupUrl, target, "width=500,height=700,scrollbars=yes");
}

interface FetchTokenParams {
	tokenEndpoint: string;
	clientId: string;
	redirectUri: string;
	codeVerifier: string;
	authCode: string;
}

export async function fetchAuthToken({
	tokenEndpoint,
	clientId,
	redirectUri,
	codeVerifier,
	authCode,
}: FetchTokenParams): Promise<AuthTokenResponse> {
	const body: BodyInit = new URLSearchParams({
		grant_type: "authorization_code",
		client_id: clientId,
		code: authCode,
		redirect_uri: redirectUri,
		code_verifier: codeVerifier,
	});

	const response = await fetch(tokenEndpoint, {
		method: "POST",
		headers: {
			"Content-Type": "application/x-www-form-urlencoded",
		},
		body,
	});

	if (!response.ok) {
		throw new Error(`Failed to fetch token: ${response.statusText}`);
	}

	const data: AuthTokenResponse = await response.json();
	console.log("Token response from OIDC provider:", data);

	if (!data.id_token) {
		throw new Error("No ID token received from token endpoint");
	}
	return data;
}

export class InactivityDetector {
	private timer: ReturnType<typeof setTimeout> | null = null;
	private timeout: number;

	constructor(
		timeoutMinutes: number,
		private onInactive: () => void,
	) {
		this.timeout = timeoutMinutes * 60 * 1000;
		this.resetTimer();
	}

	resetTimer() {
		if (this.timer) clearTimeout(this.timer);
		this.timer = setTimeout(this.onInactive, this.timeout);
	}

	destroy() {
		if (this.timer) clearTimeout(this.timer);
	}
}
